import {ImageMaterial} from './rendering';
import {
  EncompassPointCloud,
  PointCloudManager,
  RawPointCloud,
  E57PointCloud
} from './pointcloud';
import {CompassControls, PanoControls} from './controls';
import {CameraList, CameraLoadOptions} from './cameras';
import {CameraMarkers} from './markers';
import {
  AerialMap,
  extentFromPoints
} from './aerial';
import {AssetType} from "../../redux/assets-slice";
import {Clamp, ColorMapKey, ColorType, MarkerNavType} from '../../redux/settings-slice';
import LocalScene, { DataProjectionCoordinate, SceneCoordinate } from './projections';
import {colorMaps} from "../textures/colormaps";
import {
  MeasurementController,
  Measurement
} from './measurements';
import { ChangeDetector } from './utilities';
import { isDevMode } from '../../electron-modules';
import slash from 'slash';
import GPUPicker from './gpu-picker';
import { debounce, throttle } from 'throttle-debounce';
import { SceneCameraState } from '../../redux/camera-slice';
import { ImageLabelTools } from './labelling';
import {
  Vector3,
  PerspectiveCamera,
  WebGLRenderer,
  TextureLoader
} from "three";
import Stats from '../../stats.module.js';
import { RenderUtils, BasicRenderTarget } from './rendering';
import { MultiImageWindow } from './controls';
import onresize from "resize-event";
import Tags from './tags';
import {toast} from '../../app';
import { updateMouseCoords } from '../../app-bar';
import { CADHandler } from './linework';

export class Viewer {
    public readonly containerElement: HTMLElement;
    public defaultFarPlane: number = 10000;
    private defaultNearPlane: number = 0.1;
    public pixelRatio = 1.0;
    public camera: PerspectiveCamera;
    public renderer: WebGLRenderer;
    private imageRenderMaterial: ImageMaterial;
    private imageRenderTarget: BasicRenderTarget;
    public gpuPickers: {
      default: GPUPicker,
      markers: GPUPicker,
      tags: GPUPicker
    };
    public imageLabels: ImageLabelTools;
    private renderUtils: RenderUtils;
    private changeDetector: ChangeDetector;
    public compass: CompassControls;
    public controls: PanoControls;
    public pointclouds: PointCloudManager;
    public cameraList: CameraList;
    public minimap: AerialMap;
    public markers: CameraMarkers;
    public measurements: MeasurementController;
    public cadHandler: CADHandler;
    public tags: Tags;
    public multiImageWindow: MultiImageWindow;
    private stats: StatsPanel;
    private updateSavedState: Function;
    private updateMouseCoords: Function;
    private enabled = true;
    private focused = false;
    private drawPickerScene = false;
    public expanded = false;
    private initialZoomApplied = false;
    private orbitDefaultApplied = false;
    public projectName;
    private observer;

    public timingDebug = {
      "markers": false,
      "scene": false
    };

    constructor(viewContainer, mapContainer, props) {
      const {setCameraState} = props;

      this.containerElement = viewContainer;

      this.initRenderer();
      this.initGpuPickers();
      this.initMaterials();
      this.initViewEvents();
      this.updateCamera();

      // Need to debounce to update ui smoothly
      this.updateSavedState = debounce(100, () => {
        setCameraState(this.sceneState);
      });

      this.updateMouseCoords = throttle(50, (event) => {
        let coords = event
          ? this.controls.getPickerResults(event).coordinate
          : null;

        updateMouseCoords(coords);
      });

      this.compass = new CompassControls(this, props);
      this.pointclouds = new PointCloudManager(this);
      this.controls = new PanoControls(this, this.camera,
        this.renderer.domElement, props);
      this.measurements = new MeasurementController(this, props);
      this.cameraList = new CameraList(this);
      this.minimap = new AerialMap(this, mapContainer, props);
      this.tags = new Tags(this);
      this.cadHandler = new CADHandler(this);
      this.imageLabels = new ImageLabelTools(this, props);
      this.markers = new CameraMarkers(this);
      this.changeDetector = new ChangeDetector(this);
      this.multiImageWindow = new MultiImageWindow(this, props);
      this.stats = new StatsPanel(this);

      this.addContainerElements();
      this.initResize();
      this.animate();
    }

    get width(): number {
      return this.containerElement.offsetWidth;
    }

    get height(): number {
      return this.containerElement.offsetHeight;
    }

    get near() {
      return this.camera.near;
    }

    get far() {
      return this.camera.far;
    }

    get fov() {
      return this.camera.fov;
    }

    get orthoList() {
      return this.minimap.orthoList;
    }

    get lookat() {
      let lookat = new Vector3(0,0, -1);
      let quat = this.camera.quaternion;
      lookat.applyQuaternion(quat);
      return lookat;
    }

    get orbitState(): Boolean {
      return this.controls.orbit.state;
    }

    get cameraSelected() {
      return this.cameraList.cameraLoaded;
    }

    get hasPoints() {
      return !this.pointclouds.isEmpty;
    }

    get hasCameras() {
      return !this.cameraList.isEmpty;
    }

    get hasModels() {
      return this.cadHandler.numberOfFiles > 0;
    }

    get hasTags() {
      return this.tags.numberOfFiles > 0;
    }

    get isImageOnly() {
      return this.hasCameras && !this.hasPoints;
    }

    get isPointsOnly() {
      return !this.hasCameras && this.hasPoints;
    }

    get calculatingSceneMarkers() {
      return this.markers.calculating;
    }

    get calculatingAerialMarkers() {
      return this.minimap.calculating;
    }

    get showCompassElement() {
      if (!LocalScene.initialized) {
        return false;
      }

      let show = this.orbitState; // Default condition

      if (this.hasCameras) {
        // Current camera is done loading (or there is no camera selected)
        let currentCamera = this.cameraList.current;
        show = currentCamera
          ? currentCamera.loaded || currentCamera.loading
          : true;
      }

      return show;
    }

    get initialCameraLoading() {
      return this.cameraList.newCameraLoading
        && !this.cameraList.cameraLoadedOnce;
    }

    get aerialState() {
      return this.minimap.aerialState;
    }

    get sceneState() {
      let state = {
        camera: null,
        fov: null,
        lookat: null,
        angles: null,
        orbit: null,
        pivot: null
      } as SceneCameraState;

      if (this.orbitState) {
        // Update for orbit camera
        let {orbit, pivot} = this.controls.orbit.getPositionValues();

        state = {
          ...state,
          orbit: orbit,
          pivot: pivot
        };
      } else if (this.cameraList.current) {
        // Update for standard camera
        state = {
          ...state,
          camera: this.cameraList.current.id,
          fov: this.controls.fov,
          angles: this.controls.angles
        };
      }

      return state;
    }

    /** Exposed Global Methods */
    setExpanded(state) {
      this.expanded = state;
    }

    setContainerCursor(cursor) {
      this.containerElement.style.cursor = cursor;
    }

    addContainerElements() {
      this.containerElement.appendChild(this.renderer.domElement);
      this.containerElement.appendChild(this.compass.domElement);
    }

    removeContainerElements() {
      this.containerElement.removeChild(this.renderer.domElement);
      this.containerElement.removeChild(this.compass.domElement);
    }

    setProjectName(name) {
      this.projectName = name;
    }

    togglePickerDebug(value) {
      this.drawPickerScene = value;
    }

    getAvailableClassifications(): number[] {
      return this.pointclouds.uniqueClassifications;
    }

    setVisibleClassifications(classifications) {
      classifications.forEach(classification => {
        this.pointclouds.setClassification(classification.id, classification.visible);
      });
    }

    setPointCloudOpacity(opacity: number) {
      this.pointclouds.transparency = opacity;
    }

    setPointCloudSize(size: number) {
      this.pointclouds.materialDefaults.size = size;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.size = size;
        });
      });
    }

    setPointCloudMaxPoints(points: number) {
      this.pointclouds.visiblePointsTarget = points * 1000000;
    }

    setPointCloudDistance(distance: number) {
      let value = (distance > 999) ? this.far : distance;
      this.pointclouds.materialDefaults.distance = value;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.distance = value;
        });
      });
    }

    setPointCloudHeightClip(clamp: Clamp) {
      this.pointclouds.updateClamping('heightClamp', 'height_clamp', clamp);
    }

    setPointCloudIntensityClip(clamp: Clamp) {
      this.pointclouds.updateClamping('intensityClamp', 'intensity_clamp', clamp);
    }

    setPointCloudColorMap(colorMapKey: ColorMapKey) {
      const textureData = colorMaps.find(colorMap => colorMap.id === colorMapKey).image;

      const loader = new TextureLoader();
      loader.load(textureData, texture => {
        this.pointclouds.materialDefaults.colorMap = colorMapKey;
        this.pointclouds.clouds.forEach(pointCloud => {
          pointCloud.materials.forEach(material => {
            material.color_map = texture;
            material.color_map.needsUpdate = true;
          });
        });
      });
    }

    setPointCloudColorType(colorType: ColorType) {
      this.pointclouds.materialDefaults.colorType = colorType;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.color_type = colorType;
        });
      });
    }

    setPointCloudDynamicSize(state: boolean) {
      this.pointclouds.materialDefaults.dynamicSize = state;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.dynamic_size = state;
        });
      });
    }

    setPointCloudCircularPoints(state: boolean) {
      this.pointclouds.materialDefaults.circularPoints = state;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.circularPoints = state;
        });
      });
    }

    setNavigationType(state: MarkerNavType) {
      this.markers.setNavigationType(state);
    }

    setControlType(type: number) {
      this.controls.setControlType(type);
    }

    toggleOrbitState() {
      this.controls.toggleCamera();
    }

    resetCamera() {
      this.controls.resetCamera();
    }

    /** NOTE: Temporary(?) solution for the viewer being created over and over again */
    destroy() {
      this.observer.disconnect();

      // Destroy viewer elements
      this.enabled = false;
      this.camera = null;
      this.removeContainerElements();
      this.destroyViewEvents();

      // Destroy minimap dom element
      this.minimap.destroy();

      // Stop orthoList update loop
      this.orthoList.destroy();

      this.multiImageWindow.destroy();

      // TODO: destroy other things
    }

    setDataProjection(newProjection) {
      LocalScene.setDataProjection(newProjection);

      // Reset all measurements (not able to convert easily. would be easier if we changed
      // the encompass style data to be written in its original units, and converted on the fly
      if (this.controls.measurements.enabled) {
        this.controls.measurements.disable(true);
        this.controls.measurements.enable();
      }
    }

    setViewProjection(newProjection) {
      LocalScene.setViewProjection(newProjection);
    }

    setMeasurementUnits(units) {
      this.controls.measurements.setUnits(units);
    }

    async setCameraVisible(cameraID, opts:CameraLoadOptions = {}) {
      this.initialZoomApplied = true;
      let newCamera = await this.cameraList.loadImage(cameraID, opts);
      return newCamera;
    }

    setCameraDefault(state: SceneCameraState) {
      this.cameraList.loadSavedCameraState(state);
    }

    setOrbitDefault(state: SceneCameraState) {
      const shouldUpdate = (state.orbit !== null)
        && (state.pivot !== null)
        && !this.orbitDefaultApplied;

      if (!shouldUpdate) {
        return;
      }

      let orbit = new DataProjectionCoordinate(state.orbit)
        .toScene();

      let pivot = new DataProjectionCoordinate(state.pivot)
        .toScene();

      this.controls.setOrbitState(true, {
        position: orbit,
        pivot: pivot,
        smooth: false
      });

      this.initialZoomApplied = true;
      this.orbitDefaultApplied = true;

      return true;
    }

    clearCadHighlight() {
      this.cadHandler.clearHighlight();
    }

    getAerialZoomLevel() {
      return this.minimap?.map.getView().getZoom();
    }

    getCameraPosition() {
      return this.camera.position.clone();
    }

    /** Point Markup */
    pointMarkupToggle(state) {
      this.controls.pointMarkup.setState(state);
    }

    pointMarkupReset() {
      this.controls.pointMarkup.reset();
    }

    pointMarkupSelected() {
      return this.controls.pointMarkup.selected;
    }

    pointMarkupExportData() {
      return this.controls.pointMarkup.getMarkupData(true);
    }

    pointMarkupCanExport() {
      return this.controls.pointMarkup.canExport;
    }

    undoPointMarkup() {
      this.controls.pointMarkup.undoOperation();
    }

    hidePointMarkup() {
      this.controls.pointMarkup.hideSelection();
    }

    reclassifyPointMarkup(classification) {
      this.controls.pointMarkup.reclassifySelection(classification);
    }

    setPointMarkupIgnoreGround(state) {
      this.controls.pointMarkup.setIgnoreGround(state);
    }

    /** Multi Image Window */
    initMultiImageContainer(container) {
      this.multiImageWindow.init(container);
    }

    toggleMultiImageWindow(state) {
      this.multiImageWindow.setState(state);
    }

    multiImageNextImage() {
      this.multiImageWindow.loadNextImage();
    }

    multiImagePreviousImage() {
      this.multiImageWindow.loadPreviousImage();
    }

    multiImageOnComplete() {
      this.multiImageWindow.onComplete();
    }

    multiImageOnAddMore() {
      this.multiImageWindow.onAddMore();
    }

    multiImageClosePrompt() {
      return this.multiImageWindow.closePrompt();
    }

    /** Measurements */
    toggleMeasurements(state) {
      this.controls.measurements.setState(state);
    }

    updateMeasureType(measureType) {
      this.controls.measurements.setMeasurementType(measureType);
    }

    updateMeasureHover(measurement: Measurement, state) {
      this.controls.measurements.updateRenderColor(measurement, state);
    }

    deleteMeasurement(measurement: Measurement) {
      this.controls.measurements.removeByID(measurement.id);
    }

    renameMeasurement(measurement: Measurement, title) {
      this.controls.measurements.setTitle(measurement.id, title);
    }

    exportMeasurements(type, ordering) {
      this.controls.measurements.export(type, ordering);
    }

    getMeasureExportType() {
      return this.controls.measurements.measureExportType;
    }

    getMeasurementFormatter() {
      return this.controls.measurements.formatter;
    }

    loadMeasurementData(data) {
      return this.controls.measurements.loadMeasurementData(data);
    }

    toggleCameraAligner(state) {
      this.controls.cameraAlignerAdv.setState(state);
    }

    /** Ortho Viewer */
    setOrthoOpacity(value) {
      this.orthoList.setOpacity(value);
    }

    /** Labelling/Annotation */
    activeImageLabelPaths() {
      return this.imageLabels.activeImagePaths();
    }

    activeImages() {
      return this.imageLabels.activeImages();
    }

    loadedImageLabelPaths() {
      return this.imageLabels.loadedImagePaths();
    }

    async loadImageLabelData(data, progressCallback, overwrite, checkOverlap) {
      await this.imageLabels.loadImageLabelData(
        data, progressCallback, overwrite, checkOverlap);
    }

    setActiveLabelType(state) {
      this.imageLabels.setActiveLabelType(state);
    }

    labelSelected() {
      return this.imageLabels.checkIfSelected();
    }

    labelHotkey(event) {
      this.imageLabels.onKeyDown(event);
    }

    setLabelText(id, text) {
      this.imageLabels.setText(id, text);
    }

    setLabelFindText(id) {
      this.imageLabels.setFindText(id);
    }

    addCategory(category) {
      this.imageLabels.addCategory(category);
    }

    deleteCategory(id) {
      this.imageLabels.deleteCategory(id);
    }

    addTrainingArea() {
      this.imageLabels.addTrainingArea();
    }

    deleteHighlighted() {
      this.imageLabels.deleteHighlighted();
    }

    editCategoryName(id, name) {
      this.imageLabels.editCategoryName(id, name);
    }

    deleteAllCategories() {
      this.imageLabels.deleteAllCategories();
    }

    deleteTrainingArea() {
      this.imageLabels.deleteTrainingArea();
    }

    deleteLabel(id) {
      this.imageLabels.deleteLabel(id);
    }

    zoomToLabel(id) {
      this.imageLabels.zoomToLabel(id);
    }

    addLabel(id) {
      this.imageLabels.addLabel(id);
    }

    addTrainingAreaLabel() {
      this.imageLabels.addTrainingAreaLabel();
    }

    currentCategory() {
      return this.imageLabels.currentCategoryName;
    }

    getIdxInList(category) {
      return this.imageLabels.getIdxInList(category);
    }

    selectLabel(id) {
      this.imageLabels.selectLabel(id);
    }

    deleteLabelPoint(pointID) {
      this.imageLabels.deletePoint(pointID);
    }

    insertLabelPoint(id, coord) {
      this.imageLabels.insertPoint(id, coord);
    }

    editLabel(id, coord) {
      this.imageLabels.editLabel(id, coord);
    }

    moveToLabel(id) {
      this.imageLabels.moveToLabelInList(id);
    }

    completeLabel(label) {
      this.imageLabels.completeLabel(label);
    }

    unHighlight(id) {
      this.imageLabels.unHighlight(id);
    }

    unHighlightAll() {
      this.imageLabels.unHighlightAll();
    }

    sortCategory = (category, score, sortType) => {
      this.imageLabels.sortCategory(category, score, sortType);
    }

    undoSort = (category) => {
      this.imageLabels.undoSort(category);
    }

    numLabelsHighlighted() {
      return this.imageLabels.numHighlighted;
    }

    checkCategoryUniqueness(categoryName) {
      return this.imageLabels.checkUniqueName(categoryName);
    }

    labelObject = () => {
      return this.imageLabels.labelObject();
    }

    anyLabelSelected = () => {
      return this.imageLabels.checkIfSelected();
    }

    getLasFileInfo(id) {
      const pointCloud = this.pointclouds.getByID(id);
      if (!pointCloud) {
        return;
      }

      if (!(pointCloud instanceof RawPointCloud)) {
        return;
      }

      return pointCloud.headerFileInfo;
    }

    /** Asset Click */
    snapToOrtho(id) {
      const ortho = this.minimap.orthoList.getByID(id);
      if (!ortho) {
        return;
      }

      ortho.zoomToExtent();
    }

    snapToModel(id) {
      const file = this.cadHandler.getByID(id);
      if (!file) {
        return;
      }

      this.zoomToSceneExtent(file.boundingBox, {
        smooth: true,
        force: true
      });
    }

    snapToPointCloud(id) {
      const pointCloud = this.pointclouds.getByID(id);
      if (!pointCloud) {
        return;
      }

      this.zoomToSceneExtent(pointCloud.tightBoundingBox, {
        smooth: true,
        force: true
      });
    }

    snapToImages(assetID) {
      const features = this.getCameraFeaturesForCSV(assetID);
      if (features.length === 0) {
        return;
      }

      let positions = features.map(x => x.getGeometry().getCoordinates());
      let extent = extentFromPoints(positions, false);
      this.minimap.setMapExtent(extent);
    }

    zoomToSceneExtent(boundingBox, opts={}) {
      const options = {
        force: false,
        smooth: false,
        ...opts
      };

      if (!boundingBox) {
        return;
      }

      if (this.initialZoomApplied && !options.force) {
        return;
      }

      this.initialZoomApplied = true;

      // Zoom to 3d mode extent
      let newPosition = this.controls.orbitPositionFromBounds(
        boundingBox, "top", options.smooth);
      this.controls.setOrbitState(true, newPosition);

      // Zoom to map extent
      let positions = [boundingBox.min, boundingBox.max].map(position => {
        return new SceneCoordinate(position).toDataProjection();
      });

      let extent = extentFromPoints(positions);
      this.minimap.setMapExtent(extent);
    }


    /** Camera Aligner */
    resetCameraAligner() {
      this.controls.cameraAlignerAdv.reset();
    }

    checkIfAligned() {
      return this.cameraList.aligned;
    }

    addRowCameraAligner() {
      this.controls.cameraAlignerAdv.addRow();
    }

    setErrorsCameraAligner(errors) {
      this.controls.cameraAlignerAdv.setErrors(errors);
    }

    getErrorCameraAligner(i) {
      return this.controls.cameraAlignerAdv.getError(i);
    }

    deleteRowCameraAligner(i) {
      this.controls.cameraAlignerAdv.deleteRow(i);
    }

    getImageObsCameraAligner(i) {
      return this.controls.cameraAlignerAdv.getImageObs(i);
    }

    getPointObsCameraAligner(i) {
      return this.controls.cameraAlignerAdv.getPointObs(i);
    }

    getCamPoseCameraAligner(i) {
      return this.controls.cameraAlignerAdv.getCamPose(i);
    }

    getNumEntriesCameraAligner() {
      return this.controls.cameraAlignerAdv.numEntries;
    }

    getCamIdFromAligner(i) {
      return this.controls.cameraAlignerAdv.getCamId(i);
    }

    setBasicAlignerState(state) {
      this.controls.cameraAlignerBasic.setState(state);
    }

    resetBasicAlignerCamera(values = [0,0,0]) {
      this.controls.cameraAlignerBasic.reset(values);
    }

    getAlignerCameraName(i) {
      return this.controls.cameraAlignerAdv.getCamName(i);
    }

    setObservationTypeCameraAligner(type) {
      this.controls.cameraAlignerAdv.setObservationType(type);
    }

    setCurrentRowCameraAligner(rowNum) {
      this.controls.cameraAlignerAdv.setCurrentRow(rowNum);
    }

    flashObservations(rowNum) {
      this.controls.cameraAlignerAdv.flashObservations(rowNum);
    }

    hideCurrentObservation(type, rowNum) {
      this.controls.cameraAlignerAdv.hideCurrentObservation(type, rowNum);
    }

    getObservationPosition(rowNum) {
      return this.controls.cameraAlignerAdv.getObservationPosition(rowNum);
    }

    /** Panoramic/Planar image functions */

    getCurrentCamera() {
      return this.cameraList.current;
    }

    getCurrentCameraName() {
      return this.cameraList.current?.name;
    }

    getCurrentCameraIndex() {
      return this.cameraList.cameras
        .indexOf(this.cameraList.current);
    }

    getNextCamera(index) {
      let numCameras = this.cameraList.cameras.length;
      return (index + 1) % numCameras;
    }

    getPreviousCamera(index) {
      let numCameras = this.cameraList.cameras.length;
      return (index + (numCameras - 1)) % numCameras;
    }

    updateCompassOffset(offset) {
      this.compass.updateOffset(offset);
    }

    getCameraFeaturesForCSV = (assetID) => {
      return this.minimap.getByCSV(assetID);
    }

    getCamerasForCSV = (assetID) => {
      return this.cameraList.getByCSV(assetID);
    }

    getTagsForCSV = (assetID) => {
      return this.tags.getByID(assetID).tags;
    }

    getModelStatus(assetID) {
      let file = this.cadHandler.getByID(assetID);

      return {
        modelPercent: file ? file.percent : 0.0,
        modelMissing: file ? file.missing : false
      };
    }

    getCloudStatus(assetID) {
      let cloud = this.pointclouds.getByID(assetID);

      return {
        cloudPercent: cloud instanceof RawPointCloud ? cloud.percent : 0.0,
        cloudMissing: cloud ? cloud.missing : false
      };
    }

    getCloudIsLarge(assetID) {
      let cloud = this.pointclouds.getByID(assetID);
      if (cloud && cloud instanceof RawPointCloud) {
        return cloud.isLargeFile;
      }

      return false;
    }

    checkShiftReset() {
      const cameraList = this.cameraList.cameras;
      const cloudList = this.pointclouds.clouds;
      const numTags = this.tags.numberOfFiles;
      const numModels = this.cadHandler.numberOfFiles;

      const refresh = (cameraList.length === 0)
        && (cloudList.length === 0)
        && (numTags === 0)
        && (numModels === 0);

      if (!refresh) {
        return;
      }

      this.resetInitialZoom();
      LocalScene.resetSceneShift();
    }

    reconcileAssets(assets, cameraHeight, basicAdjustments, savedCameraState: SceneCameraState) {
      const {camerasFiles, pointClouds, orthoFiles, tagFiles, modelFiles} = assets;

      const [numClouds, numCloudsAdded, numCloudsRemoved] =
        this.reconcilePointClouds(pointClouds);

      const numTagsAdded = this.reconcileTags(tagFiles);

      const numModelsAdded = this.reconcileModels(modelFiles);

      const [numCameras, numCamerasAdded, numCamerasRemoved] =
        this.reconcileCameras(camerasFiles, cameraHeight, basicAdjustments);

      this.reconcileOrthos(orthoFiles);

      const allCameras = this.cameraList.cameras;
      const sceneAssetsAdded = numCloudsAdded + numTagsAdded + numModelsAdded;
      const numCamerasChanged = numCamerasAdded + numCamerasRemoved;

      // Try to apply default orbit position
      if ((sceneAssetsAdded > 0) && this.setOrbitDefault(savedCameraState)) {
        return;
      }

      // Try to load saved camera from project config
      if ((numCamerasAdded > 0) && !this.cameraList.cameraLoaded) {
        this.setCameraDefault(savedCameraState);
      }

      // All pointclouds have been removed
      if ((numClouds === 0) && (numCloudsRemoved > 0) && (numCameras > 0)) {
        if (this.cameraList.cameraLoaded) {
          // Exit 3d mode if camera is loaded
          this.controls.setOrbitState(false);
        } else {
          // Load first camera
          this.setCameraVisible(allCameras[0].id);
        }
      }

      // All cameras have been removed, go back to orbit mode
      if ((numCameras === 0) && (numCamerasRemoved > 0)) {
        this.cameraList.setOrbitCamera();
        this.controls.resetSceneView();
        this.minimap.setActiveCamera();
        this.markers.clearArrowNavigation();
      }

      // Load first camera if nothing is loaded
      const loadFirstCamera = (numCamerasChanged > 0)
        && !this.cameraList.cameraLoaded
        && !this.cameraList.newCameraLoading
        && (allCameras.length > 0);

      if (loadFirstCamera) {
        this.setCameraVisible(allCameras[0].id);
      }
    }

    reconcileCameras(camerasFiles, cameraHeight, cameraAdjustments) {
      const existingCameras = this.cameraList.cameras;
      const allCameras = camerasFiles.map(cameraFile => {
        if (!cameraFile.visible) {
          return [];
        }

        return cameraFile.data.map(camera => {
          return {
            ...camera,
            id: `${cameraFile.id}-${camera.name}`,
            csv: cameraFile.id,
            assetType: cameraFile.type,
            fromJSON: cameraFile.fromJSON,
          };
        });
      }).flat();

      const newCameras = new Set(allCameras.map(camera => camera.id));
      const currentCameras = new Set(existingCameras.map(camera => camera.id));
      const camerasToLoad = allCameras.filter(camera => !currentCameras.has(camera.id));
      const camerasToDelete = existingCameras.filter(camera => !newCameras.has(camera.id));

      const numRemoved = camerasToDelete.length;
      const numAdded = camerasToLoad.length;

      // Delete existing cameras
      this.cameraList.deleteCameras(camerasToDelete);

      // Add all new cameras
      this.cameraList.addCameras(camerasToLoad,
        cameraAdjustments, cameraHeight);

      const cameraList = this.cameraList.cameras;
      const numCameras = cameraList.length;

      // Redraw cameras in scene and aerial view after changes
      if ((numRemoved > 0) || (numAdded > 0)) {
        this.minimap.addCameraMarkers(cameraList);
        this.markers.addCameraMarkers(cameraList);
        this.controls.measurements.refreshMeasurementList();
      }

      // Update arrow marker navigation
      if ((numRemoved > 0) || ((numAdded > 0) && (numAdded < numCameras))) {
        this.markers.updateArrowNavigation();
      }

      return [numCameras, numAdded, numRemoved];
    }

    reconcilePointClouds(pointClouds) {
      const cloudList = this.pointclouds.clouds;
      const currentPointClouds = new Set(cloudList.map(cloud => cloud.identifier));
      const newPointClouds = new Set(pointClouds.map(cloud => cloud.id));

      const pointCloudsToLoad = pointClouds.filter(cloud => !currentPointClouds.has(cloud.id));
      const pointCloudsToDelete = cloudList.filter(cloud => !newPointClouds.has(cloud.identifier));

      const numRemoved = pointCloudsToDelete.length;
      const numAdded = pointCloudsToLoad.length;

      // Delete clouds that need to be removed
      pointCloudsToDelete.forEach(pointCloud => {
        this.pointclouds.deletePointCloud(pointCloud.identifier);
      });

      // Add new pointclouds
      pointCloudsToLoad.forEach(pointCloud => {
        const cloudDetails = {
          name: pointCloud.name,
          identifier: pointCloud.id,
          path: slash(pointCloud.data.path),
          visible: pointCloud.visible,
          fromJSON: pointCloud.fromJSON
        };

        if (pointCloud.type === AssetType.LAS) {
          let pointCloud = new RawPointCloud(this.pointclouds);
          pointCloud.loadFile(cloudDetails);
        } else if (pointCloud.type === AssetType.Encompass) {
          let pointCloud = new EncompassPointCloud(this.pointclouds);
          pointCloud.loadNodes(cloudDetails);
        } else if (pointCloud.type === AssetType.E57Points) {
          let pointCloud = new E57PointCloud(this.pointclouds);
          pointCloud.loadFile(cloudDetails);
        }
      });

      // Toggle proper visibility for each cloud
      pointClouds.forEach(pointCloud => {
        this.pointclouds.toggleVisibility(pointCloud.id, pointCloud.visible);
      });

      const numClouds = this.pointclouds.clouds.length;

      return [numClouds, numAdded, numRemoved];
    }

    reconcileOrthos(orthos) {
      const orthosList = this.orthoList.orthos;
      const currentOrthos = new Set(orthosList.map(ortho => ortho.id));
      const newOrthos = new Set(orthos.map(ortho => ortho.id));

      const orthosToLoad = orthos.filter(ortho => !currentOrthos.has(ortho.id));
      const orthosToDelete = orthosList.filter(ortho => !newOrthos.has(ortho.id));

      // Delete orthos that need to be removed
      orthosToDelete.forEach(ortho => {
        this.orthoList.deleteOrtho(ortho.id);
      });

      // Add new orthos
      orthosToLoad.forEach(ortho => {
        const {imageBounds, imageData, imageDataSplit, imageSize,
          imageSizeOriginal, imagePath, scaleX, scaleY} = ortho.data;

        const orthoInfo = {
          id: ortho.id,
          name: ortho.name,
          corners: imageBounds,
          base64: imageData,
          splitData: imageDataSplit,
          imageSize,
          imageSizeOriginal,
          fromJSON: ortho.fromJSON,
          imagePath,
          scaleX,
          scaleY
        };

        this.orthoList.addNewOrtho(orthoInfo);
      });

      // Toggle proper visibility for each ortho
      orthos.forEach(ortho => {
        this.orthoList.toggleVisibility(ortho.id, ortho.visible);
      });
    }

    reconcileTags(tags) {
      return this.tags.reconcile(tags);
    }

    reconcileModels(models) {
      return this.cadHandler.reconcile(models);
    }

    async jumpToTag(position: number[], updateAerialPosition) {
      if (updateAerialPosition) {
        this.minimap.jumpToPosition(position);
      }

      if (position.length === 2) {
        toast.success("Jump to to aerial tag");
        return;
      }

      if (this.hasCameras) {
        const lookat = new DataProjectionCoordinate(position);
        const closestCameraID = this.cameraList.closestCameraToPosition(lookat);

        if (closestCameraID) {
          await this.setCameraVisible(closestCameraID, {lookat});
          toast.success("Jump to to scene view tag");
          return;
        } else {
          toast.warning('No camera position found near tag');
        }
      }
    }

    setCameraHeights(height) {
      this.cameraList.setCameraHeights(height);
    }

    setCameraAdjustments(adjustments) {
      this.cameraList.setCameraAdjustments(adjustments);
    }

    setCameraMatrix(matrix) {
      this.cameraList.setCameraMatrix(matrix);
      if (this.controls.cameraAlignerAdv.enabled) {
        this.controls.cameraAlignerAdv.redrawImageObsMarkers();
      }
    }

    resetCameraMatrix() {
      this.cameraList.resetCameraMatrix();
      if (this.controls.cameraAlignerAdv.enabled){
        this.controls.cameraAlignerAdv.redrawImageObsMarkers();
      }
    }

    resetInitialZoom() {
      this.initialZoomApplied = false;
    }

    resetDefaultSceneState(state) {
      if (state.orbit) {
        this.orbitDefaultApplied = false;
        this.setOrbitDefault(state);
      } else {
        this.setCameraDefault(state);
      }
    };

    resetDefaultAerialState(state) {
      this.minimap.map.updateSize();
      this.minimap.resetView(state);
    }

    resetDefaultAerialCamera() {
      this.minimap.setActiveCamera();
    }

    resetDefaultControlState() {
      this.orbitDefaultApplied = false;
      this.controls.resetSceneView();
    }

    removeInteractions() {
      const measurements = this.controls.measurements;
      if (measurements.enabled) {
        measurements.measurementAerialView.removeInteraction();
      }
    }

    /** Private Methods */
    onKeyDown(event) {
      if (!this.focused) {
        return;
      }

      event.preventDefault();
      this.cameraList.onKeyDown(event);
      this.controls.onKeyDown(event);

    }

    onKeyUp(event) {
      this.controls.onKeyUp(event);
    }

    onMouseFocus(event) {
      if (event.type === 'mouseover') {
        this.focused = true;
      } else if (event.type === 'mouseout') {
        this.focused = false;
        this.clearCoordinates();
      }
    }

    initViewEvents() {
      this.onMouseFocus = this.onMouseFocus.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);

      // Keep track of our canvas focus
      this.containerElement.addEventListener( 'mouseover', this.onMouseFocus, false );
      this.containerElement.addEventListener( 'mouseout', this.onMouseFocus, false );

      // Keydown doesnt work on canvas
      window.addEventListener( 'keydown', this.onKeyDown, false);
      window.addEventListener( 'keyup', this.onKeyUp, false);
    }

    updateCoordinates(event) {
      if (!this.focused) {
        return;
      }

      this.updateMouseCoords(event);
    }

    clearCoordinates() {
      this.updateMouseCoords(null);
    }

    destroyViewEvents() {
      this.containerElement.removeEventListener( 'mouseover', this.onMouseFocus, false );
      this.containerElement.removeEventListener( 'mouseout', this.onMouseFocus, false );
      window.removeEventListener( 'keydown', this.onKeyDown, false);
      window.removeEventListener( 'keyup', this.onKeyUp, false);
    }

    updateCamera() {
      // Update renderutils
      this.renderUtils.setCamera(this.camera);

      // Update GPU picker camera
      Object.values(this.gpuPickers).forEach(picker => {
        picker.setCamera(this.camera);
      });
    }

    updatePickers() {
      Object.keys(this.gpuPickers).forEach(name => {
        this.updatePicker(name);
      });
    }

    updatePicker(name) {
      this.gpuPickers[name].needsUpdate = true;
    }

    initRenderer() {
      this.camera = new PerspectiveCamera(90);
      this.camera.near = this.defaultNearPlane;
      this.camera.far = this.defaultFarPlane;
      this.camera.up = new Vector3(0, 0, 1);

      this.renderer = new WebGLRenderer({
        antialias: true,
        alpha: true,
        logarithmicDepthBuffer: false,
        powerPreference: "high-performance"
      });

      this.renderer.autoClear = false;
      this.renderer.setPixelRatio(this.pixelRatio);
      this.renderer.clear();

      this.renderUtils = new RenderUtils(
        this.renderer,
        this.camera,
        this.pixelRatio
      );
    }

    initGpuPickers() {
      this.gpuPickers = {
        markers: new GPUPicker(this, "markers"),
        default: new GPUPicker(this, "default"),
        tags: new GPUPicker(this, "tags")
      };

      Object.values(this.gpuPickers).forEach(picker => {
        this.renderUtils.addTarget(picker.target);
      });
    }

    initMaterials() {
      // Used for rendering panoramic as background
      this.imageRenderMaterial = new ImageMaterial();
      this.imageRenderTarget = new BasicRenderTarget(
        this.width, this.height, true);

      this.renderUtils.addTarget(this.imageRenderTarget);
    }

    initResize() {
      this.observer = onresize(this.containerElement, () => {
        this.handleWindowResize();
      });

      this.handleWindowResize();
    }

    handleWindowResize() {
      let aspect = this.width / this.height;

      this.camera.aspect = aspect;
      this.camera.updateProjectionMatrix();

      this.renderer.domElement.style.display = "none";
      this.renderer.setSize(this.width, this.height);
      this.compass?.setSize(this.width, this.height);
      this.renderer.domElement.style.display = "block";

      this.updatePickers();

      // Re-render is required or else the div will flash white
      this.render();
    }

    updateMaterial(material, values) {
      let keys = Object.keys(values);
      keys.forEach(key => {
        material[key] = values[key];
      });
    }

    renderPanoramic() {
      // Render panoramic image
      this.renderUtils.setRenderTarget(this.imageRenderTarget, true);
      this.renderer.render(this.cameraList.scene, this.camera);

      // Update material texture with results
      this.updateMaterial(this.imageRenderMaterial, {
        colorMap: this.imageRenderTarget.texture
      });

      // Render to screen and clear depth
      this.renderUtils.renderToScreen(this.imageRenderMaterial);
      this.renderer.clearDepth();
    }

    renderSceneObjects() {
      // Render objects at full opacity
      this.renderUtils.renderScenes([
        this.markers.renderInFront ? null : this.markers.scene,
        this.cadHandler.scene
      ], 1.0);

      // Render pointcloud with transparency
      this.renderUtils.renderScenes([this.pointclouds.scene],
        this.pointclouds.transparency, false);

      // Render scene labels (mixed opacity)
      let sceneLabels = this.imageLabels.sceneLabels;
      this.renderUtils.renderScenes([sceneLabels.opacityScene],
        sceneLabels.opacity);
      this.renderUtils.renderScenes([sceneLabels.standardScene], 1.0);

      // Items here are rendered on top of everything, so they can always be seen
      this.renderUtils.renderScenes([
        this.markers.renderInFront ? this.markers.scene : null,
        this.measurements.scene,
        this.controls.alignerScene,
        this.tags.scene,
      ], 1.0);
    }

    renderPickerScene() {
      this.renderUtils.setRenderTarget(null, true);

      Object.values(this.gpuPickers).forEach(picker => {
        // Force gpu picker data
        picker.needsUpdate = true;
        picker.update();

        // Render picker scene
        this.renderer.render(picker.scene, this.camera);
      });
    }

    /** @private */
    updateWithTiming(name, component, args=[]) {
      let timeStart = performance.now();
      let response = component.update(...args);
      let timeFinish = performance.now();

      let timeElapsed = timeFinish - timeStart;
      let timeUntilWarning = 10;
      if (timeElapsed > timeUntilWarning) {
        console.warn(`Slow update detected: ${name} took ${timeElapsed.toFixed(2)}ms`);
      }

      return response;
    };

    updateSceneElements() {
      this.updateWithTiming("pointclouds", this.pointclouds);
      this.updateWithTiming("measurements", this.measurements, [this.changeDetector]);
      this.updateWithTiming("controls", this.controls, [this.changeDetector]);
      this.updateWithTiming("cameraList", this.cameraList);
      this.updateWithTiming("minimap", this.minimap, [this.changeDetector]);
      this.updateWithTiming("markers", this.markers);
      this.updateWithTiming("imageLabels", this.imageLabels, [this.changeDetector]);
      this.updateWithTiming("tags", this.tags);
      this.updateWithTiming("cadHandler", this.cadHandler);
    }

    render() {
      this.renderUtils.update();
      this.changeDetector.update();

      this.updateSceneElements();

      if (this.changeDetector.changed) {
        this.updateSavedState();
      }

      if (this.drawPickerScene) {
        this.renderPickerScene();
      } else {
        this.renderPanoramic();
        this.renderSceneObjects();
      }

      // Render compass element
      this.compass.render(this.showCompassElement,
        this.camera, this.orbitState);

      this.multiImageWindow.render();
    }

    animate() {
      if (!this.enabled) {
        return;
      }

      this.stats.begin();
      this.render();
      this.stats.end();

      requestAnimationFrame(() => {
        this.animate();
      });
    }
}

class StatsPanel {
  private stats: Stats;
  private viewer: Viewer;
  private nodesVisible;
  private pointsVisible;
  private enabled = isDevMode;
  private expanded = false;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.clear();
    this.init();
  }

  get indexToShow() {
    let childCount = this.stats.dom.children.length;
    return [0, childCount-1, childCount-2];
  }

  get parentElement() {
    return document.getElementById("debug-placeholder");
  }

  clear() {
    // Remove old stats panels
    [...document.getElementsByClassName("stats")].forEach(element => {
      element.remove();
    });
  }

  init() {
    if (!this.enabled) {
      return;
    }

    this.stats = new Stats();
    this.stats.dom.classList.add("stats");
    this.setStyle();

    this.pointsVisible = this.stats.addPanel(
      new Stats.Panel('', '#ff8', '#221'));

    this.nodesVisible = this.stats.addPanel(
      new Stats.Panel('NODE', '#f8f', '#212'));

    [...this.stats.dom.children].forEach(element => {
      element.style.display = 'none';
      element.style.margin = '2px';
    });

    this.parentElement.appendChild(this.stats.dom);

    this.drawExpandedState();
    this.initEvents();
  }

  drawExpandedState() {
    // Hide all elements
    [...this.stats.dom.children].forEach(element => {
      element.style.display = 'none';
    });

    this.indexToShow.forEach(index => {
      this.stats.dom.children[index].style.display = 'inline-block';
    });

    if (this.expanded) {
      [...this.stats.dom.children].forEach(element => {
        element.style.display = 'inline-block';
      });
    }
  }

  initEvents() {
    this.stats.dom.onclick = (event) => {
      event.preventDefault();
      this.expanded = !this.expanded;
      this.drawExpandedState();
    };
  }

  setStyle() {
    this.stats.dom.style.position = "";
    this.stats.dom.style.top = "";
    this.stats.dom.style['z-index'] = "";
  }

  begin() {
    if (!this.enabled) {
      return;
    }

    this.stats.begin();
  }

  end() {
    if (!this.enabled) {
      return;
    }

    let pointCloud = this.viewer.pointclouds;
    let numberNodes = pointCloud.numSceneNodesVisible;
    let visiblePoints = pointCloud.numVisiblePoints;
    let maxPoints = pointCloud.visiblePointsTarget;

    this.pointsVisible.update(
      visiblePoints / 1000,
      maxPoints / 1000
    );

    this.nodesVisible.update(numberNodes, 500);

    this.stats.end();
  }
}